import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { MeetingPreferences } from '../../../../../models/meeting-preferences';
import { MeetingService } from '../../../../../services/meeting.service';
import { UserMeetingPreferencesService } from '../../../../../services/user-meeting-preferences.service';
import { Meeting } from '../../../../../models/meeting';
import {
  isDateFullyBlocked,
  blockedWindowsOn,
} from '../../../../../helpers/meeting-preferences-helpers';
import { latestBookableDate, pickerMaxDate } from '../../../../../helpers/meeting-dates';

/** Weekday names in JS `Date.getDay()` order (Sunday=0), for matching against `DayAvailability.day`. */
export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Minimum prep gap enforced between meetings when generating slots. Mirrored
 * server-side in meeting.controller.js for the authoritative check. */
const BUFFER_MINUTES = 15;

export interface DialogFaculty {
  id: number;
  name: string;
  // The counselor's user id — meeting preferences are keyed by user, not counselor.
  userId: number;
}

export interface RequestMeetingDialogData {
  counselors: DialogFaculty[];
  existingMeetings: Meeting[];
}

export interface TimeSlot {
  time: Date;
  blocked: boolean;
}

export interface BookedPattern {
  dayOfWeek: number;
  startHour: number;
  startMinute: number;
  durationMinutes: number;
}

/**
 * Whether a candidate slot overlaps one of the booked patterns for the same
 * weekday, once each booking's BUFFER_MINUTES prep window is applied on
 * both sides.
 */
export function slotIsBlocked(
  slotHour: number,
  slotMin: number,
  duration: number,
  patterns: BookedPattern[],
  dow: number,
): boolean {
  const slotStart = slotHour * 60 + slotMin;
  const slotEnd = slotStart + duration;
  return patterns.some((p) => {
    if (p.dayOfWeek !== dow) return false;
    const bookedStart = p.startHour * 60 + p.startMinute - BUFFER_MINUTES;
    const bookedEnd = p.startHour * 60 + p.startMinute + p.durationMinutes + BUFFER_MINUTES;
    return slotStart < bookedEnd && slotEnd > bookedStart;
  });
}

/**
 * Whether a candidate slot overlaps one of the counselor's blocked-off
 * windows for that day. Unlike `slotIsBlocked`, this adds no BUFFER_MINUTES
 * padding — a counselor's blocked time is an exact boundary they set
 * themselves, not a meeting that needs prep room around it.
 */
export function slotOverlapsBlockedWindow(
  slotStart: number,
  slotEnd: number,
  windows: { startMinutes: number; endMinutes: number }[],
): boolean {
  return windows.some((w) => slotStart < w.endMinutes && slotEnd > w.startMinutes);
}

/**
 * The earliest bookable minute-of-day for `date`. On today that's the current
 * clock time — a slot that has already started can't be requested — and on any
 * future date the whole day is fair game.
 * @param date - The day being offered.
 * @param now - Injectable for tests; defaults to the real clock.
 */
export function earliestMinutesOn(date: Date, now: Date = new Date()): number {
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay ? now.getHours() * 60 + now.getMinutes() : 0;
}

/** A Date as a local "YYYY-MM-DD" key, matching the counts map's keys. */
function toDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Whether the faculty member has already reached their configured daily
 * meeting cap (if any) for the given date, based on a date-keyed count map
 * fetched from the server. A null/unset cap means never capped.
 */
export function isAtDailyMeetingCap(
  date: Date,
  countsByDate: Record<string, number>,
  meetingsPerDay: number | null,
): boolean {
  if (!meetingsPerDay) return false;
  return (countsByDate[toDateKey(date)] ?? 0) >= meetingsPerDay;
}

@Component({
  selector: 'app-request-meeting-dialog',
  standalone: true,
  providers: [provideNativeDateAdapter(), DatePipe],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    DatePipe,
  ],
  templateUrl: './meeting-request.html',
  styleUrl: './meeting-request.css',
})
export class RequestMeetingDialogComponent {
  private fb = inject(FormBuilder);
  private meetingService = inject(MeetingService);
  private prefsService = inject(UserMeetingPreferencesService);
  private datePipe = inject(DatePipe);
  protected dialogRef = inject(MatDialogRef<RequestMeetingDialogComponent>);
  protected data = inject<RequestMeetingDialogData>(MAT_DIALOG_DATA);

  /** Today at midnight — same-day requests are allowed, and `buildSlots` and
   *  `dateFilter` between them drop any time that has already passed. */
  protected readonly earliestDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  /** Upper bound on what's actually bookable — enforced in dateFilter below. */
  protected readonly latestDate = latestBookableDate();

  /** Structural bound fed to <mat-calendar>'s [maxDate]; see pickerMaxDate()
   *  for why this has to be wider than latestDate. */
  protected readonly pickerMaxDate = pickerMaxDate();

  protected form = this.fb.group({
    counselorId: this.fb.control<number | null>(null, Validators.required),
    durationMinutes: this.fb.control<number | null>(null, Validators.required),
    meetingType: this.fb.control<'remote' | 'in-person' | null>(null, Validators.required),
    titleTopic: this.fb.control<string | null>(null, Validators.required),
    titleOther: this.fb.control<string>('', [Validators.maxLength(50)]),
    reason: this.fb.control<string>('', [Validators.required, Validators.maxLength(200)]),
  });

  /** Common meeting titles for the dropdown; "Other" reveals a free-text field. */
  protected readonly titleTopics = [
    'Course selection',
    'College applications',
    'Career guidance',
    'Academic support',
    'Scholarships & financial aid',
    'Personal / wellbeing',
    'Schedule change',
    'Other',
  ];

  // The app runs zoneless, so anything assigned from an async callback (an HTTP
  // response rather than a click) has to be a signal — a plain field would be
  // updated without ever notifying the scheduler, leaving the view stale until
  // some unrelated event happens to trigger change detection. Fields below that
  // only ever change inside a click handler can stay plain.
  protected allowedDurations = signal<number[]>([]);
  protected selectedDate: Date | null = null;
  protected selectedSlot: Date | null = null;
  protected availableSlots: TimeSlot[] = [];
  protected inDayView = false;
  protected submitting = signal(false);

  /** Once true, the dialog swaps its booking form for the instructions popup. */
  protected submitted = signal(false);
  /** Held back so closing the popup still hands the meeting to the list. */
  private createdMeeting: Meeting | null = null;
  /** Captured at submit time — the form is no longer on screen in the popup. */
  protected submittedCounselorName = signal('');
  /** Server-side rejection message. Set from an async callback, so it has to be
   * a signal or the message never reaches the screen under zoneless CD. */
  protected submitError = signal<string | null>(null);

  // Fetched from the API for the selected counselor; null until loaded.
  private preferences = signal<MeetingPreferences | null>(null);

  /**
   * The selected counselor's standing meeting instructions, shown to the
   * student before they book. Read live off the fetched preferences rather
   * than stored on the meeting, so a counselor's edit reaches every upcoming
   * booking — these describe current logistics ("check in at the front desk"),
   * not a promise made at request time. Null hides the box entirely.
   */
  protected get counselorInstructions(): string | null {
    return this.preferences()?.instructions ?? null;
  }
  // Per-date meeting counts for the selected counselor, across all students —
  // used to grey out days that have hit their daily meeting cap. Empty until
  // loaded, which just means no day appears capped yet.
  private meetingCounts: Record<string, number> = {};

  constructor() {
    if (this.data.counselors.length === 1) {
      this.form.controls.counselorId.setValue(this.data.counselors[0].id);
      this.loadPreferences();
    }
  }

  /**
   * Fetches the selected counselor's meeting preferences (keyed by their user
   * id) and resets the duration/calendar selection to match.
   */
  private loadPreferences(): void {
    const id = this.form.controls.counselorId.value;
    const counselor = this.data.counselors.find((c) => c.id === id);
    this.form.controls.durationMinutes.setValue(null);
    this.resetCalendar();
    if (!counselor) {
      this.preferences.set(null);
      this.allowedDurations.set([]);
      this.meetingCounts = {};
      return;
    }
    this.prefsService.getUserMeetingPreferences(counselor.userId).subscribe({
      next: (prefs) => {
        const digested = MeetingPreferences.digest(prefs);
        this.preferences.set(digested);
        this.allowedDurations.set(digested.allowedDurations ?? []);
      },
      error: () => {
        this.preferences.set(null);
        this.allowedDurations.set([]);
      },
    });
    this.meetingCounts = {};
    this.meetingService.getMeetingCountsByDate(counselor.userId).subscribe({
      next: (counts) => {
        this.meetingCounts = counts;
      },
      error: () => {
        this.meetingCounts = {};
      },
    });
  }

  /** Clears all calendar/slot selection state, e.g. after switching counselors or duration. */
  private resetCalendar(): void {
    this.selectedDate = null;
    this.selectedSlot = null;
    this.availableSlots = [];
    this.inDayView = false;
  }

  /** Reloads preferences for the newly selected counselor. */
  protected onCounselorChange(): void {
    this.loadPreferences();
  }

  protected onDurationChange(): void {
    this.selectedSlot = null;
    // If already in the day view, rebuild slots for the new duration without leaving.
    if (this.inDayView && this.selectedDate) {
      this.availableSlots = this.buildSlots(this.selectedDate);
    } else {
      this.resetCalendar();
    }
  }

  /**
   * Calendar date filter passed to `<mat-calendar>`. Returns true only for
   * dates that fall on a day the counselor is available and where at least
   * one interval is long enough to fit the selected duration.
   * @param date - The date to test, or null.
   * @returns Whether the date is selectable on the calendar.
   */
  protected dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const prefs = this.preferences();
    if (!prefs) return false;
    const duration = this.form.controls.durationMinutes.value;
    if (!duration) return false;

    if (isDateFullyBlocked(prefs.blockedAvailabilityIntervals, date)) return false;
    if (isAtDailyMeetingCap(date, this.meetingCounts, prefs.meetingsPerDay)) return false;

    const day = prefs.weeklyAvailability.find(
      (d) => d.enabled && d.day === DAY_NAMES[date.getDay()],
    );
    if (!day) return false;
    if (date > this.latestDate) return false;

    // On today, only the part of each window still ahead of us can fit a
    // meeting, so a day whose remaining time is too short drops off the
    // calendar rather than opening to a list of expired slots.
    const earliest = earliestMinutesOn(date);
    return day.intervals.some((interval) => {
      if (!interval.start || !interval.end) return false;
      const start = Math.max(
        interval.start.getHours() * 60 + interval.start.getMinutes(),
        earliest,
      );
      const end = interval.end.getHours() * 60 + interval.end.getMinutes();
      return end - start >= duration;
    });
  };

  /**
   * Called when the user picks a date on the `<mat-calendar>`. Stores the date
   * and resets any previously selected slot and day-view state.
   * @param date - The date the user tapped, or null.
   */
  protected onDateSelected(date: Date | null): void {
    this.selectedDate = date;
    this.selectedSlot = null;
    this.inDayView = false;
  }

  /** Builds the day's time slots for the selected date and enters the day view. */
  protected goToDayView(): void {
    if (!this.selectedDate) return;
    this.availableSlots = this.buildSlots(this.selectedDate);
    this.inDayView = true;
  }

  /**
   * Switches back from the day (time-slot) view to the month calendar view
   * and clears any slot selection.
   */
  protected backToMonthView(): void {
    this.inDayView = false;
    this.selectedSlot = null;
  }

  /**
   * Builds the list of time slots for a given date based on the counselor's
   * weekly availability and any already-booked patterns. Each slot is marked
   * blocked if it overlaps an existing meeting, including its buffer.
   * @param date - The date to generate slots for.
   * @returns Array of TimeSlot objects with a time and blocked flag.
   */
  private buildSlots(date: Date): TimeSlot[] {
    const prefs = this.preferences();
    const duration = this.form.controls.durationMinutes.value;
    if (!prefs || !duration) return [];

    const day = prefs.weeklyAvailability.find(
      (d) => d.enabled && d.day === DAY_NAMES[date.getDay()],
    );
    if (!day) return [];

    const dow = date.getDay();

    const counselorId = this.form.controls.counselorId.value!;
    const facultyUserId = this.data.counselors.find((c) => c.id === counselorId)?.userId;

    const sessionPatterns: BookedPattern[] = this.data.existingMeetings
      .filter((m) => {
        if (!m.start || m.facultyUserId !== facultyUserId) return false;
        if (m.status === 'canceled' || m.status === 'declined') return false;
        return (
          m.start.getFullYear() === date.getFullYear() &&
          m.start.getMonth() === date.getMonth() &&
          m.start.getDate() === date.getDate()
        );
      })
      .map((m) => {
        const dur = m.end ? Math.round((m.end.getTime() - m.start!.getTime()) / 60_000) : duration;
        return {
          dayOfWeek: dow,
          startHour: m.start!.getHours(),
          startMinute: m.start!.getMinutes(),
          durationMinutes: dur,
        };
      });

    const allPatterns = sessionPatterns;
    const blockedWindows = blockedWindowsOn(prefs.blockedAvailabilityIntervals, date);
    // Times earlier today are dropped outright rather than shown struck
    // through — they aren't taken, they're simply no longer offerable.
    const earliest = earliestMinutesOn(date);

    const slots: TimeSlot[] = [];
    for (const interval of day.intervals) {
      if (!interval.start || !interval.end) continue;
      const startMinutes = interval.start.getHours() * 60 + interval.start.getMinutes();
      const endMinutes = interval.end.getHours() * 60 + interval.end.getMinutes();
      for (let m = startMinutes; m + duration <= endMinutes; m += duration) {
        if (m < earliest) continue;
        const h = Math.floor(m / 60);
        const min = m % 60;
        const time = new Date(date);
        time.setHours(h, min, 0, 0);
        slots.push({
          time,
          blocked:
            slotIsBlocked(h, min, duration, allPatterns, dow) ||
            slotOverlapsBlockedWindow(m, m + duration, blockedWindows),
        });
      }
    }
    return slots;
  }

  /**
   * Selects a time slot if it is not blocked. No-op if the slot is already taken.
   * @param slot - The TimeSlot the user clicked.
   */
  protected selectSlot(slot: TimeSlot): void {
    if (!slot.blocked) this.selectedSlot = slot.time;
  }

  /**
   * Toggles the "Other" free-text title field's required validator based on
   * whether the "Other" topic is selected.
   */
  protected onTitleTopicChange(): void {
    const other = this.form.controls.titleOther;
    if (this.form.controls.titleTopic.value === 'Other') {
      other.setValidators([Validators.required, Validators.maxLength(50)]);
    } else {
      other.clearValidators();
      other.setValue('');
    }
    other.updateValueAndValidity();
  }

  /**
   * Switches the subject field back from the "Other" free-text input to the
   * dropdown, in the same form-field slot.
   */
  protected resetSubject(): void {
    this.form.controls.titleTopic.setValue(null);
    this.onTitleTopicChange();
  }

  /**
   * Submits the meeting request for the selected counselor, duration, and
   * slot. No-op if the form is invalid or no slot has been selected.
   */
  protected submit(): void {
    if (this.form.invalid || !this.selectedSlot) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const counselor = this.data.counselors.find((c) => c.id === v.counselorId);
    if (!counselor) return;
    // Use the free-text title when "Other" is chosen, otherwise the selected topic.
    const title = v.titleTopic === 'Other' ? (v.titleOther ?? '').trim() : (v.titleTopic ?? '');

    this.submitting.set(true);
    this.submitError.set(null);
    this.meetingService
      .requestMeeting({
        facultyUserId: counselor.userId,
        facultyName: counselor.name,
        scheduledStart: this.selectedSlot,
        durationMinutes: v.durationMinutes!,
        meetingType: v.meetingType!,
        title,
        reason: (v.reason ?? '').trim(),
      })
      .subscribe({
        next: (created: Meeting) => {
          this.createdMeeting = created;
          this.submittedCounselorName.set(counselor.name);
          // Nothing to show, so don't interrupt with an empty popup — close
          // straight through to the meetings list as before.
          if (!this.counselorInstructions) {
            this.dialogRef.close(created);
            return;
          }
          // Shrink the wide two-column booking dialog down to a small popup.
          this.submitted.set(true);
          this.dialogRef.updateSize('440px');

          // The meeting already exists server-side, but the list only learns
          // about it from the close payload. A backdrop or Escape close would
          // pass undefined and the new request would silently not appear until
          // a refresh — so take those over and route them through finish().
          this.dialogRef.disableClose = true;
          this.dialogRef.backdropClick().subscribe(() => this.finish());
          this.dialogRef.keydownEvents().subscribe((event) => {
            if (event.key === 'Escape') this.finish();
          });
        },
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          this.submitError.set(
            err.error?.error || 'Something went wrong sending your request. Please try again.',
          );
        },
      });
  }

  /**
   * Dismisses the instructions popup, handing the created meeting back so the
   * list picks it up exactly as it would have on a direct close.
   */
  protected finish(): void {
    this.dialogRef.close(this.createdMeeting ?? undefined);
  }
}
